// The words on a wall, in the shopper's language (D-097). English, Danish, Swedish, Norwegian
// and Finnish first, then German, Dutch and French; any other language gets English. No zod
// here: the wall script on brands' sites imports the English words from this file.

export const WALL_LANGS = ["en", "da", "sv", "no", "fi", "de", "nl", "fr"] as const;
export type WallLang = (typeof WALL_LANGS)[number];

/** In the app, which is in English. */
export const LANG_NAMES: Record<WallLang, string> = {
  en: "English",
  da: "Danish",
  sv: "Swedish",
  no: "Norwegian",
  fi: "Finnish",
  de: "German",
  nl: "Dutch",
  fr: "French",
};

export const EN_WORDS = {
  allLabels: "All",
  showLabel: "Show posts",
  tagHashtag: "Use {hashtag} to share your moments",
  seenOnYou: "Seen on you",
  tagUs: "Share your moments with {account}",
  tagUsOr: "Share your moments with {account} or {hashtag}",
  followUs: "Follow us on Instagram",
  shopThisLook: "Shop this look",
  view: "View",
  addToBag: "Add to bag",
  adding: "Adding…",
  added: "Added ✓",
  tryAgain: "Try again",
  viewOnInstagram: "View on Instagram",
  previous: "Previous",
  next: "Next",
  previousPost: "Previous post",
  nextPost: "Next post",
  close: "Close",
  post: "Post",
  noSound: "No sound",
  posts: "Posts from Instagram",
  scrollSideways: "Posts, scroll sideways",
  scrollBack: "Scroll back",
  scrollForward: "Scroll forward",
  openPost: "Open post by {name}",
  openPostShop: "Open post by {name}, with products to shop",
  photoBy: "Photo by {name}",
  videoBy: "Video by {name}",
  postedBy: "Posted by {name}",
  poweredBy: "Powered by Brandwall",
  report: "Report",
  reportPost: "Report this post",
  whatsWrong: "What's wrong with this post?",
  reasonPrivacy: "I'm in this picture (or my child is) and didn't agree",
  reasonOffensive: "It's offensive or inappropriate",
  reasonSpam: "It's not what it says, or it's spam",
  reasonOther: "Something else",
  anythingElse: "Anything else? (optional)",
  yourEmail: "Your email, for a reply (optional)",
  sendReport: "Send report",
  cancel: "Cancel",
  thanks: "Thank you, the brand has been told.",
  notSent: "That didn't go through. Please try again.",
};
export type WordKey = keyof typeof EN_WORDS;
export type WallWords = Record<WordKey, string>;

const DA: WallWords = {
  allLabels: "Alle",
  showLabel: "Vis opslag",
  tagHashtag: "Brug {hashtag} på dine billeder",
  seenOnYou: "Set hos jer",
  tagUs: "Del dine øjeblikke med {account}",
  tagUsOr: "Del dine øjeblikke med {account} eller {hashtag}",
  followUs: "Følg os på Instagram",
  shopThisLook: "Shop looket",
  view: "Se",
  addToBag: "Læg i kurv",
  adding: "Lægger i kurv…",
  added: "Lagt i kurv ✓",
  tryAgain: "Prøv igen",
  viewOnInstagram: "Se på Instagram",
  previous: "Forrige",
  next: "Næste",
  previousPost: "Forrige opslag",
  nextPost: "Næste opslag",
  close: "Luk",
  post: "Opslag",
  noSound: "Uden lyd",
  posts: "Opslag fra Instagram",
  scrollSideways: "Opslag, rul til siden",
  scrollBack: "Rul tilbage",
  scrollForward: "Rul frem",
  openPost: "Åbn opslag fra {name}",
  openPostShop: "Åbn opslag fra {name}, med produkter du kan købe",
  photoBy: "Billede fra {name}",
  videoBy: "Video fra {name}",
  postedBy: "Delt af {name}",
  poweredBy: "Leveret af Brandwall",
  report: "Anmeld",
  reportPost: "Anmeld dette opslag",
  whatsWrong: "Hvad er der galt med opslaget?",
  reasonPrivacy: "Jeg er på billedet (eller mit barn er) og har ikke sagt ja",
  reasonOffensive: "Det er stødende eller upassende",
  reasonSpam: "Det er ikke, hvad det giver sig ud for, eller det er spam",
  reasonOther: "Noget andet",
  anythingElse: "Vil du tilføje noget? (valgfrit)",
  yourEmail: "Din e-mail, hvis du vil have svar (valgfrit)",
  sendReport: "Send anmeldelse",
  cancel: "Annuller",
  thanks: "Tak, brandet har fået besked.",
  notSent: "Det gik ikke igennem. Prøv igen.",
};

const SV: WallWords = {
  allLabels: "Alla",
  showLabel: "Visa inlägg",
  tagHashtag: "Använd {hashtag} på dina bilder",
  seenOnYou: "Sett hos er",
  tagUs: "Dela dina ögonblick med {account}",
  tagUsOr: "Dela dina ögonblick med {account} eller {hashtag}",
  followUs: "Följ oss på Instagram",
  shopThisLook: "Shoppa looken",
  view: "Visa",
  addToBag: "Lägg i varukorgen",
  adding: "Lägger till…",
  added: "Tillagd ✓",
  tryAgain: "Försök igen",
  viewOnInstagram: "Visa på Instagram",
  previous: "Föregående",
  next: "Nästa",
  previousPost: "Föregående inlägg",
  nextPost: "Nästa inlägg",
  close: "Stäng",
  post: "Inlägg",
  noSound: "Utan ljud",
  posts: "Inlägg från Instagram",
  scrollSideways: "Inlägg, scrolla i sidled",
  scrollBack: "Scrolla bakåt",
  scrollForward: "Scrolla framåt",
  openPost: "Öppna inlägg av {name}",
  openPostShop: "Öppna inlägg av {name}, med produkter att handla",
  photoBy: "Bild från {name}",
  videoBy: "Video från {name}",
  postedBy: "Delad av {name}",
  poweredBy: "Drivs av Brandwall",
  report: "Anmäl",
  reportPost: "Anmäl det här inlägget",
  whatsWrong: "Vad är fel med inlägget?",
  reasonPrivacy: "Jag är med på bilden (eller mitt barn är det) och har inte sagt ja",
  reasonOffensive: "Det är stötande eller olämpligt",
  reasonSpam: "Det är inte vad det utger sig för att vara, eller det är spam",
  reasonOther: "Något annat",
  anythingElse: "Något mer? (valfritt)",
  yourEmail: "Din e-postadress, om du vill ha svar (valfritt)",
  sendReport: "Skicka anmälan",
  cancel: "Avbryt",
  thanks: "Tack, varumärket har fått ditt meddelande.",
  notSent: "Det gick inte att skicka. Försök igen.",
};

const NO: WallWords = {
  allLabels: "Alle",
  showLabel: "Vis innlegg",
  tagHashtag: "Bruk {hashtag} på bildene dine",
  seenOnYou: "Sett hos dere",
  tagUs: "Del øyeblikkene dine med {account}",
  tagUsOr: "Del øyeblikkene dine med {account} eller {hashtag}",
  followUs: "Følg oss på Instagram",
  shopThisLook: "Shop looken",
  view: "Se",
  addToBag: "Legg i handlekurven",
  adding: "Legger til…",
  added: "Lagt til ✓",
  tryAgain: "Prøv igjen",
  viewOnInstagram: "Se på Instagram",
  previous: "Forrige",
  next: "Neste",
  previousPost: "Forrige innlegg",
  nextPost: "Neste innlegg",
  close: "Lukk",
  post: "Innlegg",
  noSound: "Uten lyd",
  posts: "Innlegg fra Instagram",
  scrollSideways: "Innlegg, rull sidelengs",
  scrollBack: "Rull tilbake",
  scrollForward: "Rull fremover",
  openPost: "Åpne innlegg fra {name}",
  openPostShop: "Åpne innlegg fra {name}, med produkter du kan kjøpe",
  photoBy: "Bilde fra {name}",
  videoBy: "Video fra {name}",
  postedBy: "Delt av {name}",
  poweredBy: "Levert av Brandwall",
  report: "Rapporter",
  reportPost: "Rapporter dette innlegget",
  whatsWrong: "Hva er galt med innlegget?",
  reasonPrivacy: "Jeg er på bildet (eller barnet mitt er det) og har ikke sagt ja",
  reasonOffensive: "Det er støtende eller upassende",
  reasonSpam: "Det er ikke det det gir seg ut for å være, eller det er spam",
  reasonOther: "Noe annet",
  anythingElse: "Noe mer? (valgfritt)",
  yourEmail: "E-posten din, for svar (valgfritt)",
  sendReport: "Send rapport",
  cancel: "Avbryt",
  thanks: "Takk, merkevaren har fått beskjed.",
  notSent: "Det gikk ikke gjennom. Prøv igjen.",
};

const FI: WallWords = {
  allLabels: "Kaikki",
  showLabel: "Näytä julkaisut",
  tagHashtag: "Käytä tunnistetta {hashtag} kuvissasi",
  seenOnYou: "Asiakkaidemme kuvia",
  tagUs: "Jaa hetkesi: {account}",
  tagUsOr: "Jaa hetkesi: {account} tai {hashtag}",
  followUs: "Seuraa meitä Instagramissa",
  shopThisLook: "Osta tyyli",
  view: "Katso",
  addToBag: "Lisää ostoskoriin",
  adding: "Lisätään…",
  added: "Lisätty ✓",
  tryAgain: "Yritä uudelleen",
  viewOnInstagram: "Katso Instagramissa",
  previous: "Edellinen",
  next: "Seuraava",
  previousPost: "Edellinen julkaisu",
  nextPost: "Seuraava julkaisu",
  close: "Sulje",
  post: "Julkaisu",
  noSound: "Ei ääntä",
  posts: "Julkaisuja Instagramista",
  scrollSideways: "Julkaisut, vieritä sivuttain",
  scrollBack: "Vieritä taaksepäin",
  scrollForward: "Vieritä eteenpäin",
  openPost: "Avaa käyttäjän {name} julkaisu",
  openPostShop: "Avaa käyttäjän {name} julkaisu, jossa on ostettavia tuotteita",
  photoBy: "Kuva: {name}",
  videoBy: "Video: {name}",
  postedBy: "Julkaissut {name}",
  poweredBy: "Palvelun tarjoaa Brandwall",
  report: "Ilmoita",
  reportPost: "Ilmoita tästä julkaisusta",
  whatsWrong: "Mikä tässä julkaisussa on vialla?",
  reasonPrivacy: "Olen kuvassa (tai lapseni on) enkä ole antanut lupaa",
  reasonOffensive: "Se on loukkaava tai sopimaton",
  reasonSpam: "Se ei ole sitä, miltä näyttää, tai se on roskapostia",
  reasonOther: "Jotain muuta",
  anythingElse: "Haluatko lisätä jotain? (valinnainen)",
  yourEmail: "Sähköpostisi vastausta varten (valinnainen)",
  sendReport: "Lähetä ilmoitus",
  cancel: "Peruuta",
  thanks: "Kiitos, brändille on kerrottu.",
  notSent: "Lähetys epäonnistui. Yritä uudelleen.",
};

const DE: WallWords = {
  allLabels: "Alle",
  showLabel: "Beiträge zeigen",
  tagHashtag: "Nutze {hashtag} für deine Momente",
  seenOnYou: "Gesehen bei euch",
  tagUs: "Teile deine Momente mit {account}",
  tagUsOr: "Teile deine Momente mit {account} oder {hashtag}",
  followUs: "Folge uns auf Instagram",
  shopThisLook: "Look shoppen",
  view: "Ansehen",
  addToBag: "In den Warenkorb",
  adding: "Wird hinzugefügt…",
  added: "Hinzugefügt ✓",
  tryAgain: "Erneut versuchen",
  viewOnInstagram: "Auf Instagram ansehen",
  previous: "Zurück",
  next: "Weiter",
  previousPost: "Vorheriger Beitrag",
  nextPost: "Nächster Beitrag",
  close: "Schließen",
  post: "Beitrag",
  noSound: "Ohne Ton",
  posts: "Beiträge von Instagram",
  scrollSideways: "Beiträge, seitlich scrollen",
  scrollBack: "Zurückscrollen",
  scrollForward: "Weiterscrollen",
  openPost: "Beitrag von {name} öffnen",
  openPostShop: "Beitrag von {name} öffnen, mit Produkten zum Kaufen",
  photoBy: "Foto von {name}",
  videoBy: "Video von {name}",
  postedBy: "Geteilt von {name}",
  poweredBy: "Bereitgestellt von Brandwall",
  report: "Melden",
  reportPost: "Diesen Beitrag melden",
  whatsWrong: "Was stimmt mit diesem Beitrag nicht?",
  reasonPrivacy: "Ich bin auf diesem Bild (oder mein Kind) und habe nicht zugestimmt",
  reasonOffensive: "Er ist beleidigend oder unangemessen",
  reasonSpam: "Er ist irreführend oder Spam",
  reasonOther: "Etwas anderes",
  anythingElse: "Noch etwas? (optional)",
  yourEmail: "E-Mail-Adresse für eine Antwort (optional)",
  sendReport: "Meldung senden",
  cancel: "Abbrechen",
  thanks: "Danke, die Marke wurde informiert.",
  notSent: "Das hat nicht geklappt. Bitte noch einmal versuchen.",
};

const NL: WallWords = {
  allLabels: "Alle",
  showLabel: "Toon posts",
  tagHashtag: "Gebruik {hashtag} bij je momenten",
  seenOnYou: "Gezien bij jullie",
  tagUs: "Deel je momenten met {account}",
  tagUsOr: "Deel je momenten met {account} of {hashtag}",
  followUs: "Volg ons op Instagram",
  shopThisLook: "Shop deze look",
  view: "Bekijk",
  addToBag: "In winkelmand",
  adding: "Toevoegen…",
  added: "Toegevoegd ✓",
  tryAgain: "Probeer opnieuw",
  viewOnInstagram: "Bekijk op Instagram",
  previous: "Vorige",
  next: "Volgende",
  previousPost: "Vorige post",
  nextPost: "Volgende post",
  close: "Sluiten",
  post: "Post",
  noSound: "Zonder geluid",
  posts: "Posts van Instagram",
  scrollSideways: "Posts, scroll zijwaarts",
  scrollBack: "Scroll terug",
  scrollForward: "Scroll verder",
  openPost: "Open post van {name}",
  openPostShop: "Open post van {name}, met producten om te shoppen",
  photoBy: "Foto van {name}",
  videoBy: "Video van {name}",
  postedBy: "Gedeeld door {name}",
  poweredBy: "Mogelijk gemaakt door Brandwall",
  report: "Melden",
  reportPost: "Meld deze post",
  whatsWrong: "Wat is er mis met deze post?",
  reasonPrivacy: "Ik sta op deze foto (of mijn kind) en heb geen toestemming gegeven",
  reasonOffensive: "Het is beledigend of ongepast",
  reasonSpam: "Het is niet wat het lijkt, of het is spam",
  reasonOther: "Iets anders",
  anythingElse: "Nog iets? (optioneel)",
  yourEmail: "Je e-mailadres, voor een antwoord (optioneel)",
  sendReport: "Melding versturen",
  cancel: "Annuleren",
  thanks: "Bedankt, het merk is op de hoogte gebracht.",
  notSent: "Dat is niet gelukt. Probeer het opnieuw.",
};

const FR: WallWords = {
  allLabels: "Tout",
  showLabel: "Afficher les publications",
  tagHashtag: "Utilisez {hashtag} pour partager vos moments",
  seenOnYou: "Vu chez vous",
  tagUs: "Partagez vos moments avec {account}",
  tagUsOr: "Partagez vos moments avec {account} ou {hashtag}",
  followUs: "Suivez-nous sur Instagram",
  shopThisLook: "Shopper ce look",
  view: "Voir",
  addToBag: "Ajouter au panier",
  adding: "Ajout en cours…",
  added: "Ajouté ✓",
  tryAgain: "Réessayer",
  viewOnInstagram: "Voir sur Instagram",
  previous: "Précédent",
  next: "Suivant",
  previousPost: "Publication précédente",
  nextPost: "Publication suivante",
  close: "Fermer",
  post: "Publication",
  noSound: "Sans son",
  posts: "Publications Instagram",
  scrollSideways: "Publications, faire défiler horizontalement",
  scrollBack: "Défiler en arrière",
  scrollForward: "Défiler en avant",
  openPost: "Ouvrir la publication de {name}",
  openPostShop: "Ouvrir la publication de {name}, avec des produits à acheter",
  photoBy: "Photo de {name}",
  videoBy: "Vidéo de {name}",
  postedBy: "Publié par {name}",
  poweredBy: "Propulsé par Brandwall",
  report: "Signaler",
  reportPost: "Signaler cette publication",
  whatsWrong: "Quel est le problème avec cette publication\u00a0?",
  reasonPrivacy: "Je suis sur cette photo (ou mon enfant) et je n'ai pas donné mon accord",
  reasonOffensive: "C'est choquant ou inapproprié",
  reasonSpam: "C'est trompeur, ou c'est du spam",
  reasonOther: "Autre chose",
  anythingElse: "Autre chose à ajouter\u00a0? (facultatif)",
  yourEmail: "Votre e-mail, pour une réponse (facultatif)",
  sendReport: "Envoyer le signalement",
  cancel: "Annuler",
  thanks: "Merci, la marque a été prévenue.",
  notSent: "L'envoi n'a pas abouti. Veuillez réessayer.",
};

export const WALL_WORDS: Record<WallLang, WallWords> = {
  en: EN_WORDS,
  da: DA,
  sv: SV,
  no: NO,
  fi: FI,
  de: DE,
  nl: NL,
  fr: FR,
};

/** The words a brand can change per wall, with what they're called in Settings. */
export const EDITABLE_WORDS: [WordKey, string][] = [
  ["shopThisLook", "Heading over the products"],
  ["view", "Link to a product"],
  ["addToBag", "Add to bag button"],
  ["viewOnInstagram", "Link to the post on Instagram"],
  ["previous", "Previous button"],
  ["next", "Next button"],
  ["report", "Report link"],
];
export const WORD_MAX = 40;

/** A wall's own wording: per language, the words the brand changed. */
export type WordOverrides = Partial<Record<WallLang, Partial<Record<WordKey, string>>>>;

/**
 * The language a page says it's in ("da-DK", "nb", "fr_CA"), as one of the wall's languages,
 * or null when it's another language or none.
 */
export function pageLang(tag: string | null | undefined): WallLang | null {
  const primary = (tag ?? "").trim().toLowerCase().split(/[-_]/)[0] ?? "";
  const lang = primary === "nb" || primary === "nn" ? "no" : primary;
  return (WALL_LANGS as readonly string[]).includes(lang) ? (lang as WallLang) : null;
}

/** Every word of a wall in one language, with the brand's own wording on top. */
export function wallWords(lang: WallLang, overrides: WordOverrides = {}): WallWords {
  const own = overrides[lang] ?? {};
  const out = { ...WALL_WORDS[lang] };
  for (const [key] of EDITABLE_WORDS) {
    const v = own[key]?.trim();
    if (v) out[key] = v.slice(0, WORD_MAX);
  }
  return out;
}

/** The brand's own words with a change applied: an empty word goes back to the usual one. */
export function applyWords(current: WordOverrides, patch: WordOverrides): WordOverrides {
  const out: WordOverrides = JSON.parse(JSON.stringify(current ?? {}));
  for (const [lang, own] of Object.entries(patch) as [WallLang, Record<string, string>][]) {
    const words: Record<string, string> = { ...(out[lang] ?? {}) };
    for (const [key, v] of Object.entries(own ?? {})) {
      if (v) words[key] = v;
      else delete words[key];
    }
    if (Object.keys(words).length) out[lang] = words;
    else delete out[lang];
  }
  return out;
}

/** Fills in {name}. */
export const fill = (text: string, name: string) => text.replace("{name}", name);
